import { CurrencyPipe, DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { Address, HorizontalListData, HorizontalListFactory, PromotionType, SimpleDialogData, SimpleHorizontalListAppearanceType, SimpleHorizontalListBgColorType, SimpleHorizontalListWidgetConfigData, SubtransactionType, TransactionType, TransactionTypeString } from "common";
import { BehaviorSubject, Observable } from "rxjs";
import * as _ from "lodash";
import { map } from "rxjs/operators";
import { AccountWithRenewalData } from "../../account/account.model";
import { SalesforceUrlPipe } from "../../shared/pipes/salesforce-url.pipe";
import { TransactionData, toPayoffDestinationLine, toPayoffDestinationsText } from "../transaction.model";
import { TransactionDetailsAdapterInputData } from "./transaction-details.model";

export const getManualReversalTooOldTransactionDialogData = (reversibleDaysPeriod: number): SimpleDialogData => ({
	title: 'Handle reversal manually',
	body: `The system does NOT recalculate splits for declined transactions older than ${reversibleDaysPeriod} calendar days. This exception has to be handled manually.`,
  });

@Injectable()
export class TransactionDetailsAdapter {
	private readonly inputData$ = new BehaviorSubject<TransactionDetailsAdapterInputData>(null);

	readonly listItems$: Observable<HorizontalListData[]> = this.inputData$.pipe(
		map((data) => this.adaptListItems(data))
	);

	constructor(
		private readonly currencyPipe: CurrencyPipe,
		private readonly datePipe: DatePipe,
		private readonly salesforceUrlPipe: SalesforceUrlPipe
	) { }

	patch(patch: Partial<TransactionDetailsAdapterInputData>): void {
		const prev = this.inputData$.value ?? {} as TransactionDetailsAdapterInputData;
		this.inputData$.next({ ...prev, ...patch });
	}

	private adaptListItems = (data: TransactionDetailsAdapterInputData): HorizontalListData[] => {
		const isTransactionNew: boolean = data?.transactionPrincipal?.id ? false : true;
		const isPaymentSectionVisible = isTransactionNew && data?.transactionTypeName !== TransactionTypeString.Draw;
		const isDrawSectionVisible = isTransactionNew && data?.transactionTypeName !== TransactionTypeString.Payment;
		const hasGeolocationInfo: boolean = data?.transactionPrincipal?.customerDrawGeographicalPosition?.latitude > 0;
		const gp = data?.transactionPrincipal?.customerDrawGeographicalPosition;
		const isCurrentPayoffTotalVisible: boolean = !!data?.transactionPrincipal?.payoffTotalDiffers && !!data?.transactionPrincipal?.payoffDestinations?.length;

		return [
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Transfer from',
				displayedValue: this.getTransferFrom(data) || '-',
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Transfer to',
				displayedValue: this.getTransferTo(data) || '-',
			}),
			...this.getPayoffToItems(data?.transactionPrincipal),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Current payoff total',
				displayedValue: this.formatCurrency(data?.transactionPrincipal?.payoffTotal),
				isItemHidden: !isCurrentPayoffTotalVisible,
			}),
			HorizontalListFactory.createKeyValueRouterLinkItem({
				title: 'Loan',
				displayedValue: data?.transactionPrincipal?.loanNumber,
				routerLink: this.getLoanProfileUrl(data),
				isItemHidden: !data?.transactionPrincipal?.loanNumber,
			}),
			HorizontalListFactory.createKeyValueRouterLinkItem({
				title: 'Original loan',
				displayedValue: data?.transactionPrincipal?.originalClientId,
				routerLink: this.getOriginalLoanProfileUrl(data),
				isItemHidden: data?.transactionPrincipal?.originalLoanId === data?.transactionPrincipal?.accountId || !data?.transactionPrincipal?.originalClientId,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Principal payment',
				displayedValue: this.formatCurrency(data?.paymentPrincipal),
				isItemHidden: !isPaymentSectionVisible,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Interest payment',
				displayedValue: this.formatCurrency(data?.paymentInterest),
				isItemHidden: !isPaymentSectionVisible,
			}),
			HorizontalListFactory.createInfoStringItem({
				displayedValue: 'Loan will be re-amortized after payment is made',
				isItemHidden: !isPaymentSectionVisible || data?.promotionToApplyInfo?.promotionType !== PromotionType.ReAmortization,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Payoff interest reducement',
				displayedValue: this.formatCurrency(data?.promotionToApplyInfo?.interestRateReducement),
				isItemHidden: !isPaymentSectionVisible || data?.promotionToApplyInfo?.promotionType !== PromotionType.PayoffInterestReduction,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Disbursed amount',
				displayedValue: this.formatCurrency(data?.disbursedAmount),
				isItemHidden: !isDrawSectionVisible,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Draw fee',
				displayedValue: this.formatCurrency(data?.drawFee),
				isItemHidden: !isDrawSectionVisible,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Promotional fee reducement',
				displayedValue: this.formatCurrency(data?.promotionToApplyInfo?.drawFeeReducement),
				isItemHidden: !isDrawSectionVisible || !data?.promotionToApplyInfo,
			}),
			HorizontalListFactory.createInfoStringItem({
				displayedValue: 'New account balances',
				isItemHidden: !isTransactionNew,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'New outstanding balance',
				displayedValue: this.formatCurrency(data?.accountNewBalances?.balanceOutstanding),
				isItemHidden: !isTransactionNew,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'New available funds',
				displayedValue: this.formatCurrency(data?.accountNewBalances?.fundsAvailable),
				isItemHidden: !isTransactionNew,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'New payment due',
				displayedValue: this.formatCurrency(data?.accountNewBalances?.paymentDue),
				isItemHidden: !isTransactionNew,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Payment due date',
				displayedValue: this.formatDate(data?.accountNewBalances?.paymentDueDate),
				isItemHidden: !isTransactionNew,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Processed date',
				displayedValue: this.formatDate(data?.transactionPrincipal?.processDate),
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Initiated by',
				displayedValue: this.getInitiatorName(data) || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Created by',
				displayedValue: data?.transactionPrincipal?.creator || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Modified by',
				displayedValue: data?.transactionPrincipal?.modifier || "-",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Maintained',
				displayedValue: data?.transactionPrincipal?.maintained ? "Yes" : "No",
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'ID',
				displayedValue: data?.transactionPrincipal?.id?.toString() || "-",
			}),
			HorizontalListFactory.createKeyValueRouterLinkItem({
				title: 'Related transaction',
				displayedValue: data?.transactionPrincipal?.relatedTransactionId?.toString(),
				routerLink: this.getTransactionDetailsUrl(data?.account?.id, data?.transactionPrincipal?.relatedTransactionId),
				isItemHidden: !data?.transactionPrincipal.relatedTransactionId
			}),
			HorizontalListFactory.createKeyValueExternalLinkItem({
				title: 'Salesforce Transaction ID',
				displayedValue: data?.transactionPrincipal?.uniqueId,
				externalLink: this.salesforceUrlPipe.transform(data?.transactionPrincipal?.uniqueId),
				isItemHidden: !data?.transactionPrincipal?.uniqueId,
			}),
			HorizontalListFactory.createKeyValueExternalLinkItem({
				title: 'Geolocation',
				displayedValue: `(${gp?.latitude}, ${gp?.longitude})`,
				externalLink: `https://maps.google.com/?q=${gp?.latitude},${gp?.longitude}`,
				isItemHidden: !hasGeolocationInfo,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Geolocation address',
				displayedValue: `${gp?.city}, ${gp?.region}, ${gp?.postalCode}`,
				isItemHidden: !hasGeolocationInfo,
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Country',
				displayedValue: gp?.country,
				isItemHidden: !hasGeolocationInfo || gp?.country === 'US',
			}),
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Business Address',
				displayedValue: this.getBusinessAddress(data?.transactionPrincipal?.businessAddress),
				isItemHidden: !hasGeolocationInfo,
			}),
		]
	}

	getTransferDetailsWidgetConfigData = (): SimpleHorizontalListWidgetConfigData => {
		return {
			backgroundColorType: SimpleHorizontalListBgColorType.Grey,
			cardAppearanceType: SimpleHorizontalListAppearanceType.Outlined,
		}
	}

	private getTransferFrom(inputData: TransactionDetailsAdapterInputData): string {
		const account: AccountWithRenewalData = inputData?.account;
		const transactionPrincipal: TransactionData = inputData?.transactionPrincipal;
		const transferAmount: number = inputData?.transferAmount;

		if (!account || !account.currentState)
			return undefined;

		if (transactionPrincipal.type === TransactionType.Draw || transactionPrincipal.type === TransactionType.Premium || transactionPrincipal.type === TransactionType.Reversal)
			return `${account.productName}: ${account.loanNumber}`;
		else if (transactionPrincipal.type === TransactionType.BalanceTransfer) {
			if (transferAmount >= 0)
				return `${account.renewalForProductName}: ${account.renewalForLoanNumber}`;
			else
				return `${account.productName}: ${account.loanNumber}`;
		}
		else if (this.isThirdPartyPayoffTransaction(transactionPrincipal))
			return `${account.productName}: ${account.loanNumber}`;
		else
			return account.disbursementBankAccountData
				? (account.disbursementBankAccountData.bankName ? account.disbursementBankAccountData.bankName + ": " : " ")
				+ account.disbursementBankAccountData.accountNumber
				: "Your bank account on file";
	}

	private getTransferTo(inputData: TransactionDetailsAdapterInputData): string {
		const account: AccountWithRenewalData = inputData?.account;
		const transactionPrincipal: TransactionData = inputData?.transactionPrincipal;
		const transferAmount: number = inputData?.transferAmount;

		if (!account || !account.currentState)
			return undefined;

		if (transactionPrincipal.type === TransactionType.Draw)
			return account.disbursementBankAccountData
				? (account.disbursementBankAccountData.bankName ? account.disbursementBankAccountData.bankName + ": " : " ")
				+ account.disbursementBankAccountData.accountNumber
				: "Your bank account on file";
		else if (transactionPrincipal.type === TransactionType.BalanceTransfer) {
			if (transferAmount >= 0)
				return `${account.productName}: ${account.loanNumber}`;
			else
				return account.renewedBy
					? `${account.renewedByProductName}: ${account.renewedByLoanNumber}`
					: `${account.renewalForProductName}: ${account.renewalForLoanNumber}`;
		}
		else if (transactionPrincipal.type === TransactionType.Premium)
			return 'External account';
		else if (this.isThirdPartyPayoffTransaction(transactionPrincipal))
			return _.escape(toPayoffDestinationsText(transactionPrincipal.payoffDestinations));
		else
			return `${account.productName}: ${account.loanNumber}`;
	}

	// List values render as HTML and lender names are Salesforce text, so payoff lines are escaped.
	private getPayoffToItems(transactionPrincipal: TransactionData): HorizontalListData[] {
		return (transactionPrincipal?.payoffDestinations ?? []).map((destination) =>
			HorizontalListFactory.createKeyValueStringItem({
				title: 'Payoff to',
				displayedValue: `${_.escape(toPayoffDestinationLine(destination))} · ${this.formatCurrency(destination.amount)}`,
			})
		);
	}

	private isThirdPartyPayoffTransaction(transactionPrincipal: TransactionData): boolean {
		return transactionPrincipal.type === TransactionType.Payoff
			&& !!transactionPrincipal.subtransactions?.some((subtransaction) => subtransaction.type === SubtransactionType.ThirdPartyPayoff);
	}

	private formatCurrency(value: number): string {
		return this.currencyPipe.transform(value, "$") || "-";
	}

	private formatDate(value: Date): string {
		return this.datePipe.transform(value) || "-";
	}

	private getInitiatorName(data: TransactionDetailsAdapterInputData): string {
		if (data?.transactionPrincipal.initiator) {
			return data?.transactionPrincipal.initiator;
		} else if (data?.userCurrent) {
			return `${data?.userCurrent?.firstName} ${data?.userCurrent?.lastName}`;
		}
		return undefined;
	}

	private getBusinessAddress(address: Address): string {
		return `${address?.line1} ${address?.line2}<br>${address?.city}, ${address?.state}, ${address?.zip}`;
	}

	private getTransactionDetailsUrl(accountId: number, transactionId: number): string {
		return `/account/${accountId}/transaction/${transactionId}/view-mode`;
	}

	private getAccountProfileUrl(accountId: number): string {
		return `/account/${accountId}/profile`;
	}

	private canLinkAccountDetails(data: TransactionDetailsAdapterInputData): boolean {
		return !!data?.canReadAccounts && !!data?.canReadEntities;
	}

	private getLoanProfileUrl(data: TransactionDetailsAdapterInputData): string {
		return this.canLinkAccountDetails(data)
			? this.getAccountProfileUrl(data?.transactionPrincipal?.accountId)
			: undefined;
	}

	private getOriginalLoanProfileUrl(data: TransactionDetailsAdapterInputData): string {
		return this.canLinkAccountDetails(data) && data?.transactionPrincipal?.originalLoanId !== data?.transactionPrincipal?.accountId
			? this.getAccountProfileUrl(data?.transactionPrincipal?.originalLoanId)
			: undefined;
	}
}
